import { patch } from "@src/appV2/api/parsedApi";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { Resources } from "../types/resources";
import { WorkerDocumentStatus } from "../types/workerDocument.types";
import { getWorkerDocumentsQueryKey, WORKER_DOCUMENTS_URL } from "./useGetWorkerDocuments";

const notificationSchema = z.object({
  reason: z.string().optional(),
  note: z.string().optional(),
  pushNotification: z.boolean().optional(),
});

const updateWorkerDocumentAttributesSchema = z.object({
  displayName: z.string().optional(),
  fulfilledRequirementId: z.string().optional(),
  status: z.nativeEnum(WorkerDocumentStatus).optional(),
  expiry: z.string().datetime().optional().nullable(),
  issueDate: z.string().datetime().optional().nullable(),
  notification: notificationSchema.optional(),
  subType: z.string().optional(),
});

const updateWorkerDocumentBodySchema = z.object({
  data: z.object({
    type: z.literal(Resources.WORKER_DOCUMENTS),
    attributes: updateWorkerDocumentAttributesSchema,
  }),
});

const updateWorkerDocumentResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal(Resources.WORKER_DOCUMENTS),
    attributes: z.record(z.unknown()),
  }),
});

type UpdateWorkerDocumentAttributes = z.input<typeof updateWorkerDocumentAttributesSchema>;
export type UpdateWorkerDocumentResponse = z.infer<typeof updateWorkerDocumentResponseSchema>;

export interface UpdateWorkerDocumentParams extends UpdateWorkerDocumentAttributes {
  id: string;
}

export function useUpdateWorkerDocument(
  options?: UseMutationOptions<UpdateWorkerDocumentResponse, unknown, UpdateWorkerDocumentParams>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...attributes }: UpdateWorkerDocumentParams) => {
      const response = await patch({
        url: `${WORKER_DOCUMENTS_URL}/${id}`,
        data: {
          data: {
            type: Resources.WORKER_DOCUMENTS,
            attributes,
          },
        },
        bodySchema: updateWorkerDocumentBodySchema,
        responseSchema: updateWorkerDocumentResponseSchema,
      });
      return response.data;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: getWorkerDocumentsQueryKey() });
      options?.onSuccess?.(data, variables, context);
    },
    meta: {
      ...options?.meta,
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_WORKER_DOCUMENT_FAILURE,
    },
  });
}
